const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const GA_SCRIPT_ID = "ga-gtag";
const GA_SNIPPET_ID = "ga-gtag-inline";
const GA_COMMENT_TEXT = "Google tag (gtag.js)";
const isProd = import.meta.env.MODE === "production";
const isDebugMode = import.meta.env.VITE_GA_DEBUG_MODE === "true";

declare global {
  interface Window {
    dataLayer?: Array<unknown>;
    gtag?: (...args: unknown[]) => void;
  }
}

let isInitialized = false;

const isBrowser = typeof window !== "undefined";

export type AnalyticsEventName =
  | "diary_entry_created"
  | "diary_entry_updated"
  | "diary_entry_deleted"
  | "exercise_completed"
  | "exercise_viewed"
  | "exercise_progress_saved"
  | "exercise_progress_removed"
  | "exercise_backend_saved"
  | "support_menu_opened"
  | "support_option_selected"
  | "app_access"
  | "continue_without_registration"
  | "personal_sources_response_saved"
  | "personal_sources_response_deleted"
  | "personal_sources_responses_cleared"
  | "social_connection_created"
  | "social_connection_updated"
  | "social_connection_deleted"
  | "social_connections_cleared"
  | "positive_imagery_selection_updated"
  | "positive_imagery_reset"
  | "client_info_submitted"
  | "client_info_updated"
  | "client_info_deleted"
  | "client_info_cleared"
  | "chatbot_resource_clicked";

export interface AnalyticsEventPayload {
  value?: number;
  category?: string;
  label?: string;
  [key: string]: unknown;
}

const hasMeasurementId = () =>
  Boolean(typeof GA_MEASUREMENT_ID === "string" && GA_MEASUREMENT_ID.trim().length > 0);

const isAnalyticsEnabled = () => isBrowser && isProd && hasMeasurementId();

const ensureGaComment = (head: HTMLHeadElement) => {
  const hasComment = Array.from(head.childNodes).some(
    (node) => node.nodeType === Node.COMMENT_NODE && node.nodeValue?.trim() === GA_COMMENT_TEXT,
  );

  if (hasComment) return;

  const comment = document.createComment(` ${GA_COMMENT_TEXT} `);
  head.insertBefore(comment, head.firstChild);
};

const injectInlineSnippet = () => {
  if (!isBrowser) return false;

  const head = document.head;
  if (!head) return false;

  ensureGaComment(head);

  if (head.querySelector(`#${GA_SNIPPET_ID}`)) return true;

  const inlineScript = document.createElement("script");
  inlineScript.id = GA_SNIPPET_ID;
  inlineScript.type = "text/javascript";
  inlineScript.text = [
    "window.dataLayer = window.dataLayer || [];",
    "function gtag(){dataLayer.push(arguments);}",
    "gtag('js', new Date());",
    `gtag('config', '${GA_MEASUREMENT_ID}', { debug_mode: ${isDebugMode} });`,
  ].join("\n");

  head.appendChild(inlineScript);

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
  }

  return true;
};

const appendGaScript = () => {
  if (!isBrowser) return false;

  if (!injectInlineSnippet()) return false;
  if (document.getElementById(GA_SCRIPT_ID)) return true;

  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;

  document.head.appendChild(script);
  return true;
};


export const initializeAnalytics = () => {
  if (isInitialized || !isAnalyticsEnabled()) {
    return;
  }

  const scriptAppended = appendGaScript();
  if (!scriptAppended) {
    return;
  }

  isInitialized = true;
};

const withAnalytics = (callback: () => void) => {
  if (!isAnalyticsEnabled()) {
    return;
  }

  if (!isInitialized) {
    initializeAnalytics();
  }

  if (!isInitialized) {
    return;
  }

  callback();
};

export const trackEvent = (eventName: AnalyticsEventName, payload: AnalyticsEventPayload = {}) => {
  withAnalytics(() => {
    window.gtag?.("event", eventName, {
      ...payload,
    });
  });
};

export const analytics = {
  initialize: initializeAnalytics,
  trackEvent,
};
